import React, { useState } from 'react';
import classNames from 'classnames';
import { useCartStore } from 'store/cart';
import { sendReaction, updateReaction } from 'api/requests';
import ReactionComponent, { reaction, Reaction } from '@/components/NewCart/Reaction';
import ReactionSelected from '@/components/NewCart/ReactionSelected';
import Transition from '@/components/NewCart/Transition';
import styles from './Reactions.module.css';

const reactionsWithConfetti = [reaction.love];

const reactionsCollection = [
  { emoji: reaction.like, label: 'Like' },
  { emoji: reaction.love, label: 'Love' },
  { emoji: reaction.confusion, label: 'Not for me' },
];

const duration = 200;

const fade = {
  duration,
  defaultStyle: {
    transition: `opacity ${duration}ms ease-in-out`,
    opacity: 0,
  },
  transitionStyles: {
    entering: { opacity: 0, display: 'flex' },
    entered: { opacity: 1, display: 'flex' },
    exiting: { opacity: 1 },
    exited: { opacity: 0, display: 'none' },
  },
};

interface Props {
  productId: string;
  pickedReaction: Reaction | null;
  pickReaction(reaction: Reaction): void;
  showReactions: boolean;
  showOptions: boolean;
  showPickedReactionAnimation: boolean;
  screenWidth: number;
}

const Reactions = ({
  productId,
  pickedReaction,
  pickReaction,
  showReactions,
  showOptions,
  showPickedReactionAnimation,
  screenWidth,
}: Props) => {
  const { cart, theme } = useCartStore();
  const [primary, setPrimary] = useState<number | null>(null);

  const handleHover = (index: number | null) => {
    if (primary === null) {
      setPrimary(index);
    }
  };

  const handleAddReaction = async (reaction: Reaction) => {
    const cartId = cart?.id;

    if (!cartId) return;

    pickReaction(reaction);

    await sendReaction(cartId, {
      productId,
      reaction: reaction.emoji,
    }).then((id) => pickReaction({ ...reaction, id }));
  };

  const handleUpdateReaction = async (reaction: Reaction) => {
    // TODO: improve typescript config, can't reuse following condition as a variable
    if (!pickedReaction || !pickedReaction.id) return;

    pickReaction(reaction);

    await updateReaction(pickedReaction.id, { reaction: reaction.emoji }).then((id) => pickReaction({ ...reaction, id }));
  };

  const handlePickReaction = pickedReaction && pickedReaction.id ? handleUpdateReaction : handleAddReaction;

  const resetPrimary = () => setPrimary(null);

  return (
    <Transition show={showReactions} {...fade}>
      <div className={styles.container} onMouseLeave={resetPrimary}>
        <div className={styles.parent}>
          <>
            {reactionsCollection.map((reaction, index) => (
              <ReactionComponent
                key={`${productId}_${reaction.emoji}`}
                reaction={reaction}
                setPrimary={() => handleHover(index)}
                onClick={() => handlePickReaction(reaction)}
                className={classNames({
                  [styles.reactionWhiteText]: showPickedReactionAnimation,
                  [styles.firstIsPrimary]: primary === 0,
                  [styles.secondIsPrimary]: primary === 1,
                  [styles.thirdIsPrimary]: primary === 2,
                })}
                index={index}
                isPicked={pickedReaction?.emoji === reaction.emoji}
                show={showOptions}
              />
            ))}
            <div className={classNames(styles.background, { [styles.backgroundBlack]: showPickedReactionAnimation })} />
          </>
        </div>
        {showPickedReactionAnimation && pickedReaction && (
          <ReactionSelected
            reaction={pickedReaction}
            className={styles.pickedReaction}
            showConfetti={reactionsWithConfetti.includes(pickedReaction.emoji)}
            index={reactionsCollection.findIndex(({ emoji }) => emoji === pickedReaction.emoji)}
            screenWidth={screenWidth}
          />
        )}
      </div>
    </Transition>
  );
};

export default Reactions;
